'use client';

import * as React from 'react';

import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { cn } from './utils';

/**
 * ScrollArea - Custom scrollbar component based on Radix UI
 *
 * @description Provides consistent cross-browser scrollbar styling with auto-hide behavior.
 *
 * ## Usage Patterns
 *
 * ### Basic vertical scroll
 * ```tsx
 * <ScrollArea className="h-[300px]">
 *   {content}
 * </ScrollArea>
 * ```
 *
 * ### Horizontal scroll (requires explicit ScrollBar)
 * ```tsx
 * <ScrollArea className="w-full">
 *   <div className="flex gap-4 min-w-max">{content}</div>
 *   <ScrollBar orientation="horizontal" />
 * </ScrollArea>
 * ```
 *
 * ### Both orientations (maintain ordering: horizontal, then vertical)
 * ```tsx
 * <ScrollArea className="h-[300px] w-full">
 *   <div className="min-w-[600px]">{content}</div>
 *   <ScrollBar orientation="horizontal" />
 *   <ScrollBar orientation="vertical" />
 * </ScrollArea>
 * ```
 *
 * ### Always visible scrollbars
 * ```tsx
 * <ScrollArea type="always" className="h-[300px]">
 *   <div className="pr-4">{content}</div>
 * </ScrollArea>
 * ```
 *
 * ### Custom scrollbar thumb color (CSS variable approach)
 * ```tsx
 * // Requires CSS: [data-radix-scroll-area-thumb] { background-color: var(--scrollbar-thumb, hsl(var(--border))); }
 * <ScrollArea style={{ '--scrollbar-thumb': 'hsl(var(--primary))' } as React.CSSProperties}>
 *   {content}
 * </ScrollArea>
 * ```
 *
 * ## Common Pitfalls
 *
 * ### h-full requires parent height
 * ```tsx
 * // BAD - scrolling won't work (parent has no defined height)
 * <div>
 *   <ScrollArea className="h-full">{content}</ScrollArea>
 * </div>
 *
 * // GOOD - parent has defined height
 * <div className="h-screen">
 *   <ScrollArea className="h-full">{content}</ScrollArea>
 * </div>
 * ```
 *
 * ### Content padding for scrollbar
 * ```tsx
 * // Add pr-4 to prevent content overlap with scrollbar
 * <ScrollArea className="h-[300px]">
 *   <div className="pr-4">{content}</div>
 * </ScrollArea>
 * ```
 *
 * ## Browser Compatibility
 *
 * WARNING: On Firefox, native scrollbars may still appear in edge cases due to
 * global `::-webkit-scrollbar` styles in app.css. Always test ScrollArea changes
 * on Firefox and Safari. The Radix ScrollArea uses JavaScript-based scrollbar
 * rendering which works across all browsers, but conflicts can occur with global
 * CSS scrollbar styles.
 *
 * ## Dynamic Content Stability
 *
 * For content areas that toggle between scrollable/non-scrollable states, consider
 * using the `.scroll-area-stable` class (defined in app.css) to prevent layout shift
 * via `scrollbar-gutter: stable`.
 *
 * ## Future Consideration: Viewport Ref Exposure
 *
 * For advanced use cases (scroll restoration, programmatic scroll, infinite scroll),
 * the component could be extended to expose a viewport ref:
 * ```tsx
 * interface ScrollAreaProps {
 *   viewportRef?: React.Ref<HTMLDivElement>;
 * }
 * ```
 * This is not currently implemented. If scroll position management becomes needed,
 * consider extending the component or accessing the viewport via DOM traversal.
 *
 * @see https://www.radix-ui.com/primitives/docs/components/scroll-area
 */
const ScrollArea = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root>
>(({ className, children, ...props }, ref) => (
  <ScrollAreaPrimitive.Root ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
    <ScrollAreaPrimitive.Viewport className="h-full w-full rounded-[inherit]">{children}</ScrollAreaPrimitive.Viewport>
    <ScrollBar />
    <ScrollAreaPrimitive.Corner />
  </ScrollAreaPrimitive.Root>
));
ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName;

const ScrollBar = React.forwardRef<
  React.ElementRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>,
  React.ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.ScrollAreaScrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'flex touch-none select-none transition-colors',
      orientation === 'vertical' && 'h-full w-2.5 border-l border-l-transparent p-[1px]',
      orientation === 'horizontal' && 'h-2.5 flex-col border-t border-t-transparent p-[1px]',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.ScrollAreaThumb className="relative flex-1 rounded-full bg-border" />
  </ScrollAreaPrimitive.ScrollAreaScrollbar>
));
ScrollBar.displayName = ScrollAreaPrimitive.ScrollAreaScrollbar.displayName;

export { ScrollArea, ScrollBar };
